import React, {useState, useEffect} from 'react';
import {
  ScrollView,
  Text,
  TouchableOpacity,
  StyleSheet,
  StatusBar,
  View,
  ActivityIndicator,
  SafeAreaView,
  Platform,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Ionicons';
import Bold from '../../../typography/BoldText';
import Regular from '../../../typography/RegularText';
import Medium from '../../../typography/MediumText';
import ApiService from '../../../util/apiService';
import colors from '../../../util/colors';
import BottomBar from '../../../components/BottomBar';

const DEVOTION_RADIUS = 20;

const HomeScreen = ({navigation}) => {
  const [todaysDevotion, setTodaysDevotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('🖼️ HomeScreen mounted');
    loadTodaysDevotion();
  }, []);

  const loadTodaysDevotion = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Loading today\'s devotion from API...');
      const devotionResponse = await ApiService.getTodaysDevotion();
      console.log('📥 API Response:', devotionResponse);
      if (devotionResponse) {
        if (devotionResponse.today) {
          setTodaysDevotion(devotionResponse.today);
          console.log('✅ Today\'s devotion loaded successfully');
        } else if (devotionResponse.recent && devotionResponse.recent.length > 0) {
          setTodaysDevotion(devotionResponse.recent[0]);
          console.log('✅ Using most recent devotion as fallback');
        } else {
          setError('No devotions available.');
        }
      } else {
        setError('No devotion available for today.');
      }
    } catch (error) {
      console.error('❌ Error loading today\'s devotion from API:', error);
      setError('Failed to load devotion. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#6C4D2A" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Medium style={styles.loadingText}>Loading today\'s devotion...</Medium>
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#6C4D2A" />
        <View style={styles.errorContainer}>
          <Icon name="cloud-offline-outline" size={48} color={colors.lion} />
          <Bold style={styles.errorTitle}>Connection Error</Bold>
          <Regular style={styles.errorText}>{error}</Regular>
          <TouchableOpacity style={styles.retryButton} onPress={loadTodaysDevotion}>
            <Medium style={styles.retryButtonText}>Try Again</Medium>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#6C4D2A" />
      {/* Simple header */}
      <View style={styles.simpleHeader}>
        <Bold style={styles.simpleHeaderTitle}>Praise Devotion</Bold>
      </View>
      <View style={styles.outerContainer}>
        {/* Use smaller padding; wrapper reserves space for BottomBar */}
        <ScrollView contentContainerStyle={[styles.scrollContainer, { paddingBottom: 16 }]}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerCenter}>
              <Bold style={styles.headerTitle}>Today's Devotion</Bold>
              <Bold style={styles.dateText}>{new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</Bold>
            </View>
          </View>

          {/* Devotion Card */}
          {todaysDevotion ? (
            <View style={styles.devotionCardContainer}>
              <LinearGradient
                colors={['#6C4D2A', '#B09569', '#D0B277']}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 1}}
                style={styles.devotionBackgroundGradient}
              >
                {/* Dark overlay for better text readability */}
                <View style={styles.darkOverlay} />
                {/* Decorative corner elements */}
                <View style={styles.cornerDecoration}>
                  <Text style={styles.cornerText}>✝</Text>
                </View>
                <View style={styles.cornerDecoration2}>
                  <Text style={styles.cornerText}>✝</Text>
                </View>
                <View style={styles.devotionContent}>
                  <Bold style={styles.devotionCardTitle}>{todaysDevotion.title}</Bold>
                  <Text 
                    style={styles.devotionCardText}
                  >
                    {todaysDevotion.text || todaysDevotion.content || 'No content available'}
                  </Text>
                  <TouchableOpacity 
                    style={styles.readFullButton}
                    onPress={() => {
                      const target = Platform.OS === 'web' ? 'Home' : 'HomeRoot';
                      navigation.navigate(target, { screen: 'DailyDevotion' });
                    }}
                    activeOpacity={0.8}
                  >
                    <LinearGradient
                      colors={['rgba(255,255,255,0.2)', 'rgba(255,255,255,0.1)']}
                      style={styles.readFullButtonGradient}
                    >
                      <Icon name="book-outline" size={18} color="#FFFFFF" />
                      <Bold style={styles.readFullButtonText}>Read Full Devotion</Bold>
                      <Icon name="arrow-forward" size={16} color="#FFFFFF" />
                    </LinearGradient>
                  </TouchableOpacity>
                </View>
              </LinearGradient>
            </View>
          ) : (
            <View style={styles.noDevotionContainer}>
              <Icon name="book-outline" size={48} color={colors.lion} />
              <Bold style={styles.noDevotionTitle}>No devotion available</Bold>
              <Regular style={styles.noDevotionText}>
                Today's devotion could not be loaded. Please try again later.
              </Regular>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.previousDevotionsButton}
              onPress={() => navigation.navigate(Platform.OS === 'web' ? 'Calendar' : 'AllDevotions')}
              activeOpacity={0.8}
            >
              <LinearGradient
                colors={['#B09569', '#D0B277', '#B09569']}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 0}}
                style={styles.previousDevotionsButtonGradient}
              >
                <Icon name="time-outline" size={20} color="#FFFFFF" />
                <Bold style={styles.previousDevotionsButtonText}>View Previous Devotions</Bold>
                <Icon name="chevron-forward" size={18} color="#FFFFFF" />
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingBottom: 30,
  },
  outerContainer: {
    // Ensure the scroll view can take full height of the screen
    flex: 1,
    width: '100%',
  },
  header: {
    backgroundColor: colors.primary,
    paddingTop: 20,
    paddingBottom: 30,
    paddingHorizontal: 20,
    alignItems: 'center',
    shadowColor: colors.glassShadow,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  headerCenter: {
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 28,
    color: colors.antiqueWhite,
    marginBottom: 5,
  },
  dateText: {
    fontSize: 16,
    color: colors.accent,
  },
  devotionCardContainer: {
    marginHorizontal: 20,
    marginTop: 30,
    borderRadius: DEVOTION_RADIUS,
    overflow: 'hidden',
    shadowColor: colors.glassShadow,
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.25,
    shadowRadius: 15,
    elevation: 10,
    // Let the devotion card grow to fill the remaining viewport height
    flexGrow: 1,
    minHeight: 0,
  },
  devotionBackgroundGradient: {
    // Make gradient fill its container height
    flex: 1,
    width: '100%',
    position: 'relative',
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  cornerDecoration: {
    position: 'absolute',
    top: 20,
    left: 20,
    opacity: 0.3,
  },
  cornerDecoration2: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    opacity: 0.3,
  },
  cornerText: {
    fontSize: 24,
    color: '#FFFFFF',
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  devotionContent: {
    padding: 30,
    width: '100%',
    // Expand and push the button to the bottom of the card
    flex: 1,
    justifyContent: 'space-between',
  },
  devotionCardTitle: {
    fontSize: 28,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 20,
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 4,
    fontWeight: 'bold',
  },
  devotionCardText: {
    fontSize: 18,
    color: '#FFFFFF',
    lineHeight: 28,
    textAlign: 'justify',
    textShadowColor: 'rgba(0, 0, 0, 0.7)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
    fontWeight: '500',
    marginBottom: 20,
  },
  readFullButton: {
    marginTop: 25,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  readFullButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    gap: 10,
  },
  readFullButtonText: {
    fontSize: 16,
    color: '#FFFFFF',
    fontWeight: '600',
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  buttonContainer: {
    paddingHorizontal: 20,
    paddingTop: 30,
    gap: 20,
  },
  previousDevotionsButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: colors.glassShadow,
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.4,
    shadowRadius: 12,
    elevation: 8,
  },
  previousDevotionsButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    paddingHorizontal: 24,
    gap: 12,
  },
  previousDevotionsButtonText: {
    fontSize: 17,
    color: '#FFFFFF',
    fontWeight: '700',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  loadingText: {
    fontSize: 16,
    color: colors.lion,
    marginTop: 15,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  errorTitle: {
    fontSize: 20,
    color: colors.kobicha,
    marginTop: 15,
    marginBottom: 10,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 16,
    color: colors.lion,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 25,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: 25,
    paddingVertical: 12,
    borderRadius: 25,
    shadowColor: colors.glassShadow,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  retryButtonText: {
    fontSize: 16,
    color: colors.antiqueWhite,
    fontWeight: '600',
  },
  noDevotionContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  noDevotionTitle: {
    fontSize: 20,
    color: colors.kobicha,
    marginTop: 15,
    marginBottom: 10,
    textAlign: 'center',
  },
  noDevotionText: {
    fontSize: 16,
    color: colors.lion,
    textAlign: 'center',
    lineHeight: 24,
  },
  simpleHeader: {
    backgroundColor: colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  simpleHeaderTitle: {
    fontSize: 18,
    color: colors.antiqueWhite,
  },
});

export default HomeScreen;
